import { SpaceSkySurfaceContext } from "@-/dev.improve.haniverse.components.ui.space-backdrop";
import { LearnSummaryFlashcard, type LearnSummaryFlashcardHandle } from "#learn-summary-card";
import { useLearnSummaryFlashcardStage } from "#use-learn-summary-card-stage";
import { LearnFlashcardSwipePressHighlight } from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.card-gestures";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { Icon, type IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { GlassButton } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { ScreenReadyMarker } from "@-/dev.improve.haniverse.components.ui.screen-ready-marker";
import { ScrollEdgeFade } from "@-/dev.improve.haniverse.components.ui.scroll-edge-fade";
import { PrimaryNavigationStripFadeInsetContext } from "@-/dev.improve.haniverse.lib.primary-navigation-strip";
import {
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { LearnSummaryHeaderContext } from "#learn-summary-header-context";
import { LearnSummaryScrollTitle } from "#learn-summary-scroll-title";
import { useRevealedMorphemeGlyphTileGrid } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.link.glyphs.list";
import { LearnSummaryDueReviewCardRow } from "#learn-summary-due-review-card-row";
import type { LearnSummaryDueReviewCard } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary.navigator";
import { uniq } from "@-/com.npmjs.array-utils-ts";
import { LEARN_SUMMARY_CTA_BOTTOM_GAP } from "@-/dev.improve.haniverse.routes.nav.app.learn.summary._lib.content-bottom-inset";
import {
  ActivityIndicator,
  Pressable,
  View,
  type LayoutChangeEvent,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from "@-/com.npmjs.react-native";
import { hasLength } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { LearnSummaryDueReviewGridSkeleton } from "#learn-summary-due-review-grid-skeleton";
import split from "@-/com.npmjs.just-split";
import {
  Surface,
  SurfaceHeader,
  SurfaceTitle,
} from "@-/dev.improve.haniverse.components.ui.surface";
import { LegendList, type LegendListRef } from "@-/com.npmjs.legendapp__list";
import { useIsNavigationFocused } from "@-/dev.improve.haniverse.contexts.primary-controls-strip.opaque-chrome";
import { useNavContentWidth } from "@-/dev.improve.haniverse.routes.nav._context";
import { LearnSummaryReviewOptionsMenu } from "#learn-summary-review-options-menu";
import {
  LearnMapSheetCovered,
  useLearnMapCardSlideStyle,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import Animated from "@-/com.npmjs.react-native-reanimated";
import {
  LEARN_CARD_CLEARANCE,
  LearnFlashcardShell,
} from "@-/dev.improve.haniverse.components.ui.learn-card-shell";
import { MorphemeInlineNavigationContext } from "@-/dev.improve.haniverse.lib.morpheme.page.navigator";
import { LearnFlashcardExploration } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.card-exploration";
import type { LearnFlashcardExplorationDestination } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import useLatest from "@-/com.npmjs.use-latest";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";

import {
  CardScrollOptInContext,
  useCardScrollOptInState,
} from "@-/dev.improve.haniverse.lib.cards.review-swipe";

const DUE_REVIEW_GRID_COLUMNS = 8;
const DUE_REVIEW_GRID_GAP = 4;
/** Browse Levels: 4px list inset + 1px surface border + 4px grid inset (`px-1` twice). */
const DUE_REVIEW_GRID_SIDE_PADDING = DUE_REVIEW_GRID_GAP * 2 + 1;
/** The list's `pt-1` above the surface, matching its `px-1` inset from the card's sides. */
const LIST_TOP_INSET = 4;
/** The surface's bottom border, closing it under the last row. */
const SURFACE_BORDER = 1;
/** The footer is `h-14` with `h-11` controls centered in it: their tops sit 50pt up. */
const FOOTER_CONTROLS_TOP = 50;
/** Gap between the surface's bottom edge and the controls below it. */
const GRID_EDGE_GAP = 9;
const GRID_BOTTOM_SPACER = FOOTER_CONTROLS_TOP + GRID_EDGE_GAP;
/** The surface's body, split across the virtualized rows (as in Search's glyph grid). */
const SURFACE_BODY_CLASS_NAME = "border-x border-border/60 bg-surface px-1";

/** The distinct subjects of some due cards — the graphemes their tile data is read for. */
function dueReviewCardSubjects(cards: readonly LearnSummaryDueReviewCard[]): string[] {
  return uniq(cards.map((card) => card.subject));
}

/** Each mounted row owns only its eight tiles and their data, including after a long scroll. */
function DueReviewRow({
  availableWidth,
  cards,
  onShowAll,
  overflowCount,
}: {
  availableWidth: number | undefined;
  cards: readonly LearnSummaryDueReviewCard[];
  onShowAll: () => void;
  /** Set on the collapsed grid's last row: its final cell counts the hidden reviews. */
  overflowCount?: number;
}) {
  const isFocused = useIsNavigationFocused();
  const [measuredHeight, setMeasuredHeight] = useState<number | null>(null);
  const estimatedHeight =
    availableWidth === undefined
      ? 40
      : (availableWidth - DUE_REVIEW_GRID_GAP * (DUE_REVIEW_GRID_COLUMNS - 1)) /
          DUE_REVIEW_GRID_COLUMNS +
        DUE_REVIEW_GRID_GAP;
  // Keep the virtualizer's row shell and exact measured geometry across a tab switch. Only the
  // heavy tile/query subtree leaves; the list's scroll offset, header and route state stay alive.
  return (
    <View
      className={`pb-1 ${SURFACE_BODY_CLASS_NAME}`}
      onLayout={(event) => {
        if (isFocused && event.nativeEvent.layout.height > 0) {
          setMeasuredHeight(event.nativeEvent.layout.height);
        }
      }}
      style={isFocused ? undefined : { height: measuredHeight ?? estimatedHeight }}
      testID={
        isFocused ? "learn-summary-due-review-row" : "learn-summary-due-review-row-placeholder"
      }
    >
      {isFocused ? (
        <DueReviewRowContent
          availableWidth={availableWidth}
          cards={cards}
          onShowAll={onShowAll}
          overflowCount={overflowCount}
        />
      ) : null}
    </View>
  );
}

function DueReviewRowContent({
  availableWidth,
  cards,
  onShowAll,
  overflowCount,
}: {
  availableWidth: number | undefined;
  cards: readonly LearnSummaryDueReviewCard[];
  onShowAll: () => void;
  overflowCount?: number;
}) {
  const graphemes = useMemo(() => dueReviewCardSubjects(cards), [cards]);
  const { tileData } = useRevealedMorphemeGlyphTileGrid({ graphemeList: graphemes });
  return (
    <>
      {tileData.loadedCount === 0 ? (
        <LearnSummaryDueReviewGridSkeleton
          columns={DUE_REVIEW_GRID_COLUMNS}
          count={cards.length + (overflowCount === undefined ? 0 : 1)}
          gap={DUE_REVIEW_GRID_GAP}
        />
      ) : (
        <LearnSummaryDueReviewCardRow
          availableWidth={availableWidth}
          cards={cards}
          columns={DUE_REVIEW_GRID_COLUMNS}
          gap={DUE_REVIEW_GRID_GAP}
          href="/(nav)/(app)/learn/explore/morpheme/[morpheme]"
          tileData={tileData}
          trailingCell={
            overflowCount === undefined ? undefined : (
              <DueReviewOverflowTile count={overflowCount} onPress={onShowAll} />
            )
          }
        />
      )}
    </>
  );
}

/** Stands in for the collapsed grid's last tile, counting every review below the fold. */
function DueReviewOverflowTile({ count, onPress }: { count: number; onPress: () => void }) {
  return (
    <Pressable
      accessibilityLabel={`Show ${count} more reviews`}
      accessibilityRole="button"
      className="size-full items-center justify-center rounded-xs border border-border bg-muted/50 px-0.5 dark:bg-muted/70"
      onPress={onPress}
      testID="learn-summary-due-review-overflow"
    >
      <Text
        adjustsFontSizeToFit
        className="font-sans text-base font-semibold"
        colorVariant="muted"
        numberOfLines={1}
      >
        +{count}
      </Text>
    </Pressable>
  );
}

const dueReviewRowKey = (row: readonly LearnSummaryDueReviewCard[]) =>
  JSON.stringify(row.map((card) => card.userCardId));

/**
 * How the grid fits the card before Show all: as many whole rows as keep the surface's bottom
 * edge at least `GRID_EDGE_GAP` above the footer controls. The surface stays pinned under the
 * card's top edge; any leftover space falls between it and the controls.
 */
function getCollapsedDueReviewGrid({
  greetingHeight,
  listSize,
  rowCount,
}: {
  greetingHeight: number;
  listSize: { height: number; width: number };
  rowCount: number;
}) {
  const gridWidth = listSize.width - DUE_REVIEW_GRID_SIDE_PADDING * 2;
  const rowPitch =
    (gridWidth - DUE_REVIEW_GRID_GAP * (DUE_REVIEW_GRID_COLUMNS - 1)) / DUE_REVIEW_GRID_COLUMNS +
    DUE_REVIEW_GRID_GAP;
  // Rows carry their gap as `pb-1`, which is also the surface's inset below the last row.
  const gridSpace =
    listSize.height - (LIST_TOP_INSET + greetingHeight) - SURFACE_BORDER - GRID_BOTTOM_SPACER;
  const visibleRowCount = Math.max(1, Math.floor(gridSpace / rowPitch));
  if (rowCount <= visibleRowCount) {
    return null;
  }
  return { rowPitch, visibleRowCount };
}

/** Expands the collapsed grid in place, from the surface header (Browse → Levels' toggle spot). */
function DueReviewShowAllButton({ onPress }: { onPress: () => void }) {
  return (
    <Pressable
      accessibilityLabel="Show all"
      accessibilityRole="button"
      className="h-11 flex-row items-center gap-1 rounded-xs px-2 active:bg-accent"
      onPress={onPress}
    >
      <Text className="text-sm font-medium" colorVariant="muted">
        Show all
      </Text>
      <Icon className="text-muted-foreground" name="LuChevronDown" size={18} />
    </Pressable>
  );
}

/**
 * The learn summary's frame, shared by the loaded page AND its first-load fallback.
 *
 * The continuation summary stays centered when no reviews are due; the due count heads the review
 * grid when there is one. The CTA can appear independently. The launch greeting is not here: it
 * is on Learn's map, where the app opens (`LearnSummaryMapWelcome`).
 */
export function LearnSummaryReadyView({
  cta,
  ctaPendingLabel = null,
  dueReviewCardList,
  dueReviewLabel,
  message = null,
  notice = null,
  showReviewOptions = false,
}: {
  /**
   * The bottom action. A null handler keeps the button visible but disabled. A null CTA is
   * reserved for the exhausted-catalogue state (nothing to press).
   *
   * By default the action lets the card go (Start). With `keepsCard` it acts without dismissing
   * the card: the button calls `onPress` directly, and a swipe either way presses it while the card
   * springs home (the daily-limit congratulation's Upgrade to Premium). `icon` replaces the label
   * in landscape's button column.
   */
  cta: {
    icon?: IconName;
    keepsCard?: boolean;
    label: string;
    onPress: (() => void | false | Promise<void | false>) | null;
  } | null;
  /**
   * Shown with a spinner in place of the CTA label while the action waits on something outside
   * this screen (e.g. "Syncing from cloud…"). Pair it with a null handler, which also stops a swipe.
   */
  ctaPendingLabel?: string | null;
  dueReviewCardList: readonly LearnSummaryDueReviewCard[];
  /** `"N reviews due"`, or `null` while unknown / when nothing is due. */
  dueReviewLabel: string | null;
  /**
   * Body copy under the greeting. Carries the exhausted-catalogue message ("Nothing to learn right
   * now."), which used to be a separate full-screen view — folding it in here means that state
   * keeps the greeting and the frame instead of replacing them.
   */
  message?: string | null;
  /**
   * Richer content under the greeting and message, such as the daily new-character limit and its
   * upgrade button. Centered in the card like the welcome greeting.
   */
  notice?: ReactNode;
  showReviewOptions?: boolean;
}) {
  // Only Show all (or the "+N" tile) expands the grid; a tap on the card never does.
  const scrollOptIn = useCardScrollOptInState("learn-summary", false);
  const isExpanded = scrollOptIn.value.isScrollEnabled;
  // Tile links open inside this card, exactly like links on a review card. The welcome grid stays
  // mounted underneath with its scroll position, and swiping still starts the review session.
  const [exploration, setExploration] = useState<LearnFlashcardExplorationDestination | null>(null);
  const isExploring = exploration !== null;
  // Reference content scrolls after a tap, like a revealed review card's back.
  const explorationScrollOptIn = useCardScrollOptInState("learn-summary:exploration");
  const activeScrollOptIn = isExploring ? explorationScrollOptIn : scrollOptIn;
  const [listSize, setListSize] = useState<{ height: number; width: number } | null>(null);
  const [greetingHeight, setGreetingHeight] = useState(0);
  const listRef = useRef<LegendListRef>(null);
  const cardRef = useRef<LearnSummaryFlashcardHandle>(null);
  const cardStage = useLearnSummaryFlashcardStage();
  const { diameter, isLandscape } = useToolbarGridMetrics();
  // Every CTA row matches the header drawer/menu buttons, and the deck action row that replaces it.
  const controlsHeight = diameter;
  const [isContinuing, setIsContinuing] = useState(false);
  const isContinuingRef = useLatest(isContinuing);
  const explore = useCallback(
    (nextExploration: LearnFlashcardExplorationDestination) => {
      // Ignore a prepared link once a swipe or Start has committed the card.
      if (!isContinuingRef.current) {
        setExploration(nextExploration);
      }
    },
    [isContinuingRef],
  );
  const backToSummary = useCallback(() => setExploration(null), []);
  const ctaOnPress = cta?.onPress ?? null;
  const ctaKeepsCard = cta?.keepsCard === true;
  // A CTA that keeps the card is pressed by a swipe instead of letting the card go.
  const pressCtaKeepingCard = useMemo(
    () =>
      ctaKeepsCard && ctaOnPress !== null
        ? () => {
            void ctaOnPress();
          }
        : null,
    [ctaKeepsCard, ctaOnPress],
  );
  // The summary card leaves for the map like the deck's card (with the deck's beneath it); its
  // Start row stays put for the map's sheet to slide over.
  const mapCardSlideStyle = useLearnMapCardSlideStyle();
  // The next visit starts from the card's own content: each visit is a fresh summary.
  const onContinue = ctaOnPress === null || ctaKeepsCard ? null : ctaOnPress;
  const publishHeaderTitle = useContext(LearnSummaryHeaderContext);
  const bottomTabFadeInset = useContext(PrimaryNavigationStripFadeInsetContext);
  const rows = useMemo(
    () => split(dueReviewCardList, DUE_REVIEW_GRID_COLUMNS),
    [dueReviewCardList],
  );
  const firstRow = useMemo(() => dueReviewCardSubjects(rows[0] ?? []), [rows]);
  // Keep the centered greeting until the first row can paint its real stage/pinyin. Other rows
  // read only while mounted; scrolling never grows a permanent prefix of tile components/data.
  const { tileData } = useRevealedMorphemeGlyphTileGrid({ graphemeList: firstRow });
  // Known up front on a portrait phone, so every tile renders at its final glyph size on the first
  // commit instead of measuring itself; `null` elsewhere, where the grid measures as before.
  const navContentWidth = useNavContentWidth();
  const gridAvailableWidth =
    navContentWidth === null
      ? undefined
      : navContentWidth -
        (LEARN_CARD_CLEARANCE + 1 + DUE_REVIEW_GRID_SIDE_PADDING) * 2 -
        (isLandscape ? diameter * 3 + 27 : 0);

  // Tile content may require cloud data. Keep the greeting still until the first row is ready,
  // but counts and the CTA depend only on the local summary. Card preparation validates content
  // when pressed; decorating the review grid must never keep "Loading Reviews" on screen.
  const isFirstTilePageLoading = arrayLength(firstRow) > 0 && tileData.loadedCount === 0;
  const isCtaDisabled = cta?.onPress == null || ctaPendingLabel !== null;
  const showGrid = !isFirstTilePageLoading && !hasLength(dueReviewCardList, 0);
  // With no due reviews, keep an explicit summary above the next-card preview instead of an empty
  // card shell.
  const showContinueSummary =
    !showGrid && dueReviewLabel === null && message === null && notice === null && cta !== null;
  const isSummaryCentered = (showContinueSummary || notice !== null) && !showGrid;
  const collapsedGrid = useMemo(
    () =>
      !showGrid || listSize === null
        ? null
        : getCollapsedDueReviewGrid({ greetingHeight, listSize, rowCount: rows.length }),
    [greetingHeight, listSize, rows.length, showGrid],
  );
  const isCollapsed = collapsedGrid !== null && !isExpanded;
  const hiddenReviewCount =
    collapsedGrid === null
      ? 0
      : arrayLength(dueReviewCardList) -
        (collapsedGrid.visibleRowCount * DUE_REVIEW_GRID_COLUMNS - 1);
  const renderedRows = useMemo(() => {
    if (!showGrid) {
      return [];
    }
    if (!isCollapsed) {
      return rows;
    }
    // The last visible row gives up its final tile to the "+N" cell.
    const visibleRows = rows.slice(0, collapsedGrid.visibleRowCount);
    const lastRow = visibleRows.pop() ?? [];
    return [...visibleRows, lastRow.slice(0, -1)];
  }, [collapsedGrid, isCollapsed, rows, showGrid]);
  const expandScrollOffset = useRef<number | null>(null);
  const showAll = useCallback(() => {
    if (collapsedGrid === null) {
      return;
    }
    // Lift the first row to the card's top edge, under only the surface's own inset above it; the
    // spacer below the expanded grid guarantees the range, so the header layout never has to move.
    expandScrollOffset.current = LIST_TOP_INSET + greetingHeight - DUE_REVIEW_GRID_GAP;
    scrollOptIn.value.enableScroll();
  }, [collapsedGrid, greetingHeight, scrollOptIn.value]);
  useEffect(() => {
    const offset = expandScrollOffset.current;
    if (!isExpanded || offset === null) {
      return;
    }
    expandScrollOffset.current = null;
    // Wait a frame so the full grid and its spacer are laid out before the list scrolls.
    return safeRequestAnimationFrame(() => {
      void listRef.current?.scrollToOffset({ animated: true, offset });
    }, "learn-summary-expand-scroll");
  }, [isExpanded]);
  const renderRow = useCallback(
    ({ index, item }: { index: number; item: LearnSummaryDueReviewCard[] }) => (
      <DueReviewRow
        availableWidth={gridAvailableWidth}
        cards={item}
        onShowAll={showAll}
        overflowCount={
          isCollapsed && index === collapsedGrid.visibleRowCount - 1 ? hiddenReviewCount : undefined
        }
      />
    ),
    [collapsedGrid, gridAvailableWidth, hiddenReviewCount, isCollapsed, showAll],
  );
  // Collapsed, the spacer keeps the surface at least `GRID_EDGE_GAP` above the controls. Expanded,
  // it also guarantees the scroll range that lifts the first row to the card's top edge.
  const bottomSpacerHeight =
    collapsedGrid === null || listSize === null || !isExpanded
      ? GRID_BOTTOM_SPACER
      : Math.max(
          GRID_BOTTOM_SPACER,
          listSize.height -
            DUE_REVIEW_GRID_GAP -
            SURFACE_BORDER -
            arrayLength(rows) * collapsedGrid.rowPitch,
        );

  const scrollMetrics = useRef({ greetingY: 0, labelBottom: null as number | null, offsetY: 0 });
  const [isSummaryOffscreen, setIsSummaryOffscreen] = useState(false);
  const updateSummaryVisibility = useCallback(() => {
    const { greetingY, labelBottom, offsetY } = scrollMetrics.current;
    setIsSummaryOffscreen(labelBottom !== null && offsetY >= greetingY + labelBottom);
  }, []);
  const handleGreetingLayout = useCallback(
    (event: LayoutChangeEvent) => {
      // LegendList wraps its header; the inner layout excludes the list's `pt-1` inset. With the
      // grid, the label's column also sits under the surface's top border.
      scrollMetrics.current.greetingY =
        event.nativeEvent.layout.y + (showGrid ? LIST_TOP_INSET + SURFACE_BORDER : 0);
      setGreetingHeight(event.nativeEvent.layout.height);
      updateSummaryVisibility();
    },
    [showGrid, updateSummaryVisibility],
  );
  const handleLabelLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { height, y } = event.nativeEvent.layout;
      scrollMetrics.current.labelBottom = y + height;
      updateSummaryVisibility();
    },
    [updateSummaryVisibility],
  );
  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      scrollMetrics.current.offsetY = event.nativeEvent.contentOffset.y;
      updateSummaryVisibility();
    },
    [updateSummaryVisibility],
  );
  const headerTitle = useMemo(
    () => (
      <LearnSummaryScrollTitle
        dueReviewLabel={dueReviewLabel}
        showDueCount={showGrid && isSummaryOffscreen && dueReviewLabel !== null}
      />
    ),
    [dueReviewLabel, isSummaryOffscreen, showGrid],
  );

  useEffect(() => {
    publishHeaderTitle?.(headerTitle);
    return () => publishHeaderTitle?.(null);
  }, [headerTitle, publishHeaderTitle]);

  return (
    <View className="min-h-0 flex-1">
      <ScreenReadyMarker screen="learn-summary" />
      <Animated.View
        className="min-h-0 flex-1"
        style={[
          isLandscape
            ? { marginLeft: diameter + 9, marginRight: diameter + 9 }
            : { marginBottom: LEARN_SUMMARY_CTA_BOTTOM_GAP + bottomTabFadeInset + controlsHeight },
          mapCardSlideStyle,
        ]}
      >
        <LearnSummaryFlashcard
          onContinue={onContinue}
          onPendingChange={setIsContinuing}
          onSwipeBack={isExploring ? backToSummary : (pressCtaKeepingCard ?? undefined)}
          ref={cardRef}
          scrollEnabled={activeScrollOptIn.value.isScrollEnabled}
          stage={cardStage}
          touchHandlers={activeScrollOptIn.touchHandlers}
        >
          <MorphemeInlineNavigationContext value={explore}>
            <View
              accessibilityElementsHidden={isExploring}
              className="min-h-0 flex-1"
              importantForAccessibility={isExploring ? "no-hide-descendants" : "auto"}
              pointerEvents={isExploring ? "none" : "box-none"}
            >
              <CardScrollOptInContext value={scrollOptIn.value}>
                <LegendList
                  bounces={isExpanded && collapsedGrid !== null}
                  className="min-h-0 flex-1"
                  contentContainerClassName={
                    isSummaryCentered
                      ? "grow justify-center"
                      : showGrid
                        ? "grow items-stretch px-1 pt-1"
                        : "grow items-stretch pt-4"
                  }
                  data={renderedRows}
                  drawDistance={40}
                  estimatedItemSize={40}
                  keyExtractor={dueReviewRowKey}
                  ListFooterComponent={
                    showGrid ? (
                      <>
                        <View className={`rounded-b-xs border-b ${SURFACE_BODY_CLASS_NAME}`} />
                        <View style={{ height: bottomSpacerHeight }} />
                      </>
                    ) : null
                  }
                  ListHeaderComponent={
                    showGrid ? (
                      // The greeting heads the surface whose body holds the due tiles.
                      <View onLayout={handleGreetingLayout}>
                        <Surface className="rounded-b-none border-b-0">
                          <SurfaceHeader className="min-h-11 flex-row items-center gap-2 pr-1">
                            <View className="min-w-0 flex-1 py-2">
                              {dueReviewLabel === null ? null : (
                                <SurfaceTitle className="pt-0 text-xl" onLayout={handleLabelLayout}>
                                  {dueReviewLabel}
                                </SurfaceTitle>
                              )}
                            </View>
                            {/* Only while collapsed; `min-h-11` keeps the header (and so the fit)
                              the same height either way. */}
                            {isCollapsed ? <DueReviewShowAllButton onPress={showAll} /> : null}
                          </SurfaceHeader>
                        </Surface>
                        <View className={`h-1 ${SURFACE_BODY_CLASS_NAME}`} />
                      </View>
                    ) : showContinueSummary ||
                      dueReviewLabel !== null ||
                      message !== null ||
                      notice !== null ? (
                      <View className="items-center gap-2 px-4" onLayout={handleGreetingLayout}>
                        {showContinueSummary ? (
                          <>
                            <Text
                              className="text-center font-sans text-[28px] font-semibold"
                              colorVariant="default"
                            >
                              Ready to keep learning?
                            </Text>
                            <Text
                              className="text-center font-sans text-[17px]"
                              colorVariant="muted"
                            >
                              No reviews due. Continue with your next lesson.
                            </Text>
                          </>
                        ) : null}
                        {dueReviewLabel === null ? null : (
                          <Text
                            className="text-center font-sans text-[17px]"
                            colorVariant="muted"
                            onLayout={handleLabelLayout}
                          >
                            {dueReviewLabel}
                          </Text>
                        )}
                        {message === null ? null : (
                          <Text className="text-center font-sans text-[17px]" colorVariant="muted">
                            {message}
                          </Text>
                        )}
                        {notice}
                      </View>
                    ) : null
                  }
                  onLayout={(event) => {
                    const { height, width } = event.nativeEvent.layout;
                    setListSize((size) =>
                      size?.height === height && size.width === width ? size : { height, width },
                    );
                  }}
                  onScroll={handleScroll}
                  recycleItems={false}
                  ref={listRef}
                  renderItem={renderRow}
                  scrollEnabled={isExpanded && collapsedGrid !== null}
                  scrollEventThrottle={16}
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={isExpanded && collapsedGrid !== null}
                  testID="learn-summary-review-list"
                />
                {/* The expanded grid dissolves into the card's edge. */}
                {isExpanded && collapsedGrid !== null ? (
                  <View className="absolute inset-x-0 bottom-0" pointerEvents="none">
                    <ScrollEdgeFade />
                  </View>
                ) : null}
              </CardScrollOptInContext>
            </View>
            {isExploring ? (
              <LearnFlashcardShell className="absolute inset-0" testID="learn-summary-exploration">
                <CardScrollOptInContext value={explorationScrollOptIn.value}>
                  <LearnFlashcardExploration
                    destination={exploration}
                    onBack={backToSummary}
                    stage={cardStage}
                  />
                </CardScrollOptInContext>
              </LearnFlashcardShell>
            ) : null}
          </MorphemeInlineNavigationContext>
        </LearnSummaryFlashcard>
      </Animated.View>
      {/* Once Start commits, the deck's own controls take this row's place underneath. */}
      {isContinuing || cta === null ? null : (
        // The row floats on the Learn backdrop, which paints the sky when the space background is on.
        <SpaceSkySurfaceContext value>
          <LearnMapSheetCovered
            className={isLandscape ? "absolute right-2.25 bottom-2.25" : "absolute inset-x-0"}
            contentClassName={isLandscape ? "items-center gap-2.25" : "items-center"}
            style={
              isLandscape
                ? { top: diameter + 18, width: diameter }
                : {
                    bottom: LEARN_SUMMARY_CTA_BOTTOM_GAP + bottomTabFadeInset,
                    height: controlsHeight,
                  }
            }
          >
            {/* Exploring, a swipe presses Back to Review rather than this button. */}
            <GlassButton
              accessibilityLabel={ctaPendingLabel ?? cta.label}
              accessibilityState={{
                busy: ctaPendingLabel !== null,
                disabled: isCtaDisabled,
              }}
              className={
                isLandscape
                  ? "min-h-0 w-full flex-1 rounded-full p-0 shadow-xs"
                  : "h-full rounded-full px-6 py-0 shadow-xs"
              }
              disabled={isCtaDisabled}
              isLoading={ctaPendingLabel !== null}
              onPress={() =>
                pressCtaKeepingCard === null ? cardRef.current?.continue() : pressCtaKeepingCard()
              }
              size={isLandscape ? "fill" : undefined}
            >
              {/* A horizontal swipe either way commits Start, so dragging past a snap presses
                  it; a CTA that keeps the card is pressed by a swipe too. Exploring, the swipe
                  presses the card's Back to Review pill instead. */}
              {isCtaDisabled || isExploring ? null : (
                <LearnFlashcardSwipePressHighlight
                  className="rounded-full"
                  direction="both"
                  stage={cardStage}
                  testID="learn-summary-cta-swipe-highlight"
                />
              )}
              {ctaPendingLabel === null ? null : <ActivityIndicator size="small" />}
              {isLandscape ? (
                <Icon
                  className="text-foreground"
                  name={cta.icon ?? (cta.label === "Start Reviewing" ? "LuPlay" : "LuChevronRight")}
                  size={22}
                />
              ) : (
                <Text
                  adjustsFontSizeToFit
                  className="text-base leading-6"
                  colorVariant="default"
                  numberOfLines={1}
                >
                  {ctaPendingLabel ?? cta.label}
                </Text>
              )}
            </GlassButton>
            {showReviewOptions ? (
              <View
                className={
                  isLandscape ? "absolute inset-x-0 top-0 items-center" : "absolute right-2.25"
                }
              >
                <LearnSummaryReviewOptionsMenu />
              </View>
            ) : null}
          </LearnMapSheetCovered>
        </SpaceSkySurfaceContext>
      )}
    </View>
  );
}
